import { ArrowRightOnRectangleIcon, BeakerIcon, BoltSlashIcon, BookOpenIcon, EyeSlashIcon, InformationCircleIcon, KeyIcon, QuestionMarkCircleIcon, UserGroupIcon, WrenchIcon } from '@heroicons/react/20/solid';
import React, { useEffect, useState } from 'react';
import Layout from '../Layout';
import { CHURCH_FOUNDING_DATE } from './constants';
import DateHeadline from './DateHeadline';
import Filters from './Filters';
import { useGetLeadersQuery } from './hooks/useGetLeadersQuery';
import useRouteChanges, { RouteChangesProvider } from './hooks/useRouteChanges';
import LeaderCard from './LeaderCard';
import { DetailLevel, Gender } from './types';

const iconLegend = [
  { Icon: KeyIcon, label: 'Current prophet', iconClass: 'text-yellow-500' },
  { Icon: KeyIcon, label: 'Was prophet' },
  { Icon: UserGroupIcon, label: 'Polygamist' },
  { Icon: EyeSlashIcon, label: 'Never was an apostle' },
  { Icon: BoltSlashIcon, label: 'Apostle but never in Q12' },
  { Icon: ArrowRightOnRectangleIcon, label: 'Released early' },
];

const fullIconLegend = [
  { Icon: BookOpenIcon, label: 'College educated' },
  { Icon: BeakerIcon, label: 'Accepted evolution' },
  { Icon: QuestionMarkCircleIcon, label: 'Open on evolution' },
  { Icon: WrenchIcon, label: 'Rejected evolution' },
];

const detailLevels = [DetailLevel.SIMPLE, DetailLevel.BASIC, DetailLevel.FULL];

const LeadersContent = () => {
  const { searchParams, partialUpdateSearchParams, date, gender, detail, setDetail } = useRouteChanges();

  // set defaults filters when nothing is set
  useEffect(() => {
    if (!date && !gender) {
      partialUpdateSearchParams({ date: CHURCH_FOUNDING_DATE, gender: Gender.MALE });
    }
    // eslint-disable-next-line
  }, []);

  // the detail level is display-only; keep it out of the api query key
  const apiSearchParams = new URLSearchParams(searchParams);
  apiSearchParams.delete('detail');

  const {
    isLoading,
    isFetching,
    error,
    data
  } = useGetLeadersQuery(apiSearchParams.toString());

  const detailLevel = Object.values(DetailLevel).includes(detail.toUpperCase() as DetailLevel)
    ? detail.toUpperCase() as DetailLevel
    : DetailLevel.BASIC;

  const [showIconKey, setShowIconKey] = useState(false);
  const setDetailLevel = (level: DetailLevel) => {
    setDetail(level === DetailLevel.BASIC ? '' : level.toLowerCase());
  };

  let body = undefined;

  let leaders = [];
  let calledBys: string[] = [];
  let prevLeaderChangeDate = undefined;
  let nextLeaderChangeDate = undefined;
  let prevProphetChangeDate = undefined;
  let nextProphetChangeDate = undefined;
  let averageAge = undefined;

  if (data) {
    leaders = data.data;
    calledBys = data.meta.options.calledBys;
    prevLeaderChangeDate = data.meta.pagination.prevLeaderChangeDate;
    nextLeaderChangeDate = data.meta.pagination.nextLeaderChangeDate;
    prevProphetChangeDate = data.meta.pagination.prevProphetChangeDate;
    nextProphetChangeDate = data.meta.pagination.nextProphetChangeDate;
    averageAge = data.meta.averageAge;

    body = (
      <>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
          <Filters calledBys={calledBys} />
          <div className="inline-flex rounded border border-gray-300 dark:border-gray-600 overflow-hidden text-sm">
            {detailLevels.map(level => (
              <button
                key={level}
                className={[
                  'px-3 py-1 font-bold capitalize',
                  level === detailLevel
                    ? 'bg-gray-300 text-gray-800 dark:bg-gray-600 dark:text-white'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700',
                ].join(' ')}
                onClick={() => setDetailLevel(level)}
              >
                {level.toLowerCase()}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-gray-500 dark:text-gray-400">
          <span>{leaders.length} leaders{averageAge ? ` · average age ${averageAge}` : ''}</span>
          <button
            className="flex items-center hover:text-gray-700 dark:hover:text-gray-200"
            onClick={() => setShowIconKey(!showIconKey)}
          >
            <InformationCircleIcon className="h-4 w-4 mr-1" aria-hidden="true" />
            {showIconKey ? 'Hide icon key' : 'Icon key'}
          </button>
        </div>
        {showIconKey && <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500 dark:text-gray-400">
          {[...iconLegend, ...(detailLevel === DetailLevel.FULL ? fullIconLegend : [])].map(({ Icon, label, iconClass }: { Icon: typeof KeyIcon, label: string, iconClass?: string }) => (
            <span key={label} className="flex items-center">
              <Icon className={`h-4 w-4 mr-1 ${iconClass || ''}`} aria-hidden="true" />
              {label}
            </span>
          ))}
        </div>}
        {leaders.length === 0 ? (
          <p className="mt-8 text-center text-gray-600 dark:text-gray-400">
            No leaders match these filters. Try Show All Leaders or another date.
          </p>
        ) : (
          <div className="flex flex-wrap -mx-1 lg:-mx-4">
            {leaders.map((leader, i) => (
              <div key={i} className="my-1 px-1 w-full md:w-1/2 lg:my-4 lg:px-4 lg:w-1/3">
                <LeaderCard leader={leader} detailLevel={detailLevel} />
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  if (error || !data) {
    body = <div>There was an error.</div>;
  }

  if (isLoading) {
    body = (
      <div className="flex items-center justify-center">
        <div
          className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
          role="status">
          <span
            className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]"
          >
            Loading...
          </span>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto">
        <DateHeadline
          loading={isFetching}
          prevLeaderChangeDate={prevLeaderChangeDate}
          nextLeaderChangeDate={nextLeaderChangeDate}
          prevProphetChangeDate={prevProphetChangeDate}
          nextProphetChangeDate={nextProphetChangeDate}
        />
        {body}
      </div>
    </Layout>
  );
};

const Leaders = () => (
  <RouteChangesProvider>
    <LeadersContent />
  </RouteChangesProvider>
);

export default Leaders;
